import React, { useEffect, useState } from "react";
import DashboardSidebar from "../../components/DashboardSidebar";
import Nav from "../../components/Nav";
import Select from "react-select";
import { useParams, useNavigate, Link } from "react-router-dom";
import ApiService from "../../services/ApiService";
import ConfirmButton from "../../components/ConfirmButton";

const EditSubscription = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: "",
        type: "monthly",
        status: false,
        promotion: false,
        free: false,
        price: "",
        discount: "",
        total: "",
        features: [],
    });

    const [currentFeature, setCurrentFeature] = useState("");
    const [loading, setLoading] = useState(false);

    const getSubscription = async () => {
        if (id) {
            setLoading(true);
            try {
                const response = await ApiService.get(`/cms/subscriptions/${id}`);
                setFormData({
                    name: response?.data?.name || "",
                    type: response?.data?.type || "",
                    status: response?.data?.status || false,
                    promotion: response?.data?.promotion || false,
                    free: response?.data?.free || false,
                    price: response?.data?.price || "",
                    discount: response?.data?.discount || "",
                    total: response?.data?.total || "",
                    features: response?.data?.features || [],
                });
            } catch (error) {
                console.error("Error fetching subscription data:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        getSubscription();
    }, [id]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const calculateTotal = () => {
            let total = 0;

            const normalizedDiscount = Math.min(Math.max(formData.discount, 1), 99);

            if (formData.free) {
                total = 0;
            } else if (formData.price > 0 && !formData.promotion) {
                total = formData.price;
            } else if (formData.promotion && normalizedDiscount > 0 && normalizedDiscount < 100) {
                total = formData.price - (formData.price * normalizedDiscount) / 100;
            }

            setFormData((prev) => ({
                ...prev,
                total: (Math.ceil(total * 100) / 100)?.toFixed(2)?.toString(),
                price: (Math.ceil(formData.price * 100) / 100)?.toFixed(2)?.toString(),
                discount: normalizedDiscount?.toFixed(2)?.toString()
            }));
        };


        calculateTotal();
    }, [formData.price, formData.promotion, formData.discount, formData.free]);

    const handleFeatureKeyDown = (e) => {
        if (e.key === "," || e.key === "Enter") {
            e.preventDefault();
            const trimmedFeature = currentFeature.trim();
            if (trimmedFeature) {
                setFormData((prev) => ({
                    ...prev,
                    features: [...prev.features, trimmedFeature],
                }));
                setCurrentFeature("");
            }
        }
    };

    const handleDeleteFeature = (index) => {
        setFormData((prev) => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index),
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const dataToSubmit = {
                name: formData.name,
                type: formData.type,
                status: formData.status,
                free: formData.free,
                promotion: formData.promotion,
                price: formData.price,
                discount: formData.discount,
                total: formData.total,
                features: formData.features,
            };

            if (id) {
                await ApiService.put(`/cms/subscriptions/${id}`, { data: dataToSubmit });
            } else {
                await ApiService.post(`/cms/subscriptions`, { data: dataToSubmit });
            }
            navigate("/dashboard/subscriptions/list");
        } catch (error) {
            console.error("Error saving subscription data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await ApiService.delete(`/cms/subscriptions/${id}`);
            navigate("/dashboard/subscriptions/list");
        } catch (error) {
            console.error("Error deleting subscription:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tyn-root dashboard">
            <Nav />
            <div className="tyn-content tyn-content-full-height tyn-chatbot tyn-chatbot-page ">
                <div className="tyn-main tyn-content-inner" id="tynMain">
                    <div className="container">
                        <div className={`${id ? "edit-dashboard-nav" : "tyn-profile"} d-flex align-items-center justify-content-between px-5 py-4`} style={{ background: "white", borderRadius: "1rem" }}>
                            <div><h3 className="text-capitalize center">{id ? "Edit Subscription" : "Add New Subscription"}</h3></div>
                            <div className="buttons d-flex">
                                <button className="btn btn-primary me-2" onClick={handleSave} disabled={loading || !formData.name || !formData.type}>
                                    {id ? "Save Changes" : "Add Subscription"}
                                </button>
                                <Link className="btn btn-light" to="/dashboard/subscriptions/list" aria-label="Cancel and back">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                    </svg>
                                    Back to list
                                </Link>
                            </div>
                            <div className="back-btn-mobile" style={{ display: 'none' }}>
                                <Link className="btn btn-light" to="/dashboard/subscriptions/list" aria-label="Back to list">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                        <div className="tyn-profile">
                            <div className="tyn-profile-details mt-4">
                                <div className="tab-content">
                                    <div className="tab-pane show active" id="profile-edit" tabIndex="0">
                                        <div className="row gy-5">
                                            <div className="col-12">
                                                <div className="row gy-4">
                                                    <div className="col-lg-12">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="name">
                                                                Name *
                                                            </label>
                                                            <div className="form-control-wrap">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    id="name"
                                                                    placeholder="Subscription Name"
                                                                    value={formData.name}
                                                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-6">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="type">
                                                                Type *
                                                            </label>
                                                            <div className="form-control-wrap">
                                                                <select id="type" className="form-select" value={formData.type} onChange={(e) => handleInputChange("type", e?.target?.value)}>
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="semiannual">Semi-Annual</option>
                                                                    <option value="annual">Annual</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-6">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="price">
                                                                Price *
                                                            </label>
                                                            <div className="form-control-wrap">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    id="price"
                                                                    placeholder="Subscription Price"
                                                                    value={formData?.price}
                                                                    onChange={(e) => handleInputChange("price", e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-6">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="discount">
                                                                Discount (%)
                                                            </label>
                                                            <div className="form-control-wrap">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    id="discount"
                                                                    placeholder="Discount Percentage"
                                                                    value={formData.discount}
                                                                    onChange={(e) => handleInputChange("discount", e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>


                                                    <div className="col-3 col-lg-2">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="status">
                                                                Status
                                                            </label>
                                                            <div className="form-control-wrap position-relative">
                                                                <div className="position-absolute me-3">
                                                                    <div className="form-check form-check-reverse form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id="status"
                                                                            checked={formData.status}
                                                                            onChange={(e) => handleInputChange("status", e.target.checked)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-3 col-lg-2">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="promotion">
                                                                Promotion
                                                            </label>
                                                            <div className="form-control-wrap position-relative">
                                                                <div className="position-absolute me-3">
                                                                    <div className="form-check form-check-reverse form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id="promotion"
                                                                            checked={formData.promotion}
                                                                            onChange={(e) => handleInputChange("promotion", e.target.checked)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-3 col-lg-2">
                                                        <div className="form-group">
                                                            <label className="form-label" htmlFor="free">
                                                                Is Free
                                                            </label>
                                                            <div className="form-control-wrap position-relative">
                                                                <div className="position-absolute me-3">
                                                                    <div className="form-check form-check-reverse form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id="free"
                                                                            checked={formData.free}
                                                                            onChange={(e) => handleInputChange("free", e.target.checked)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>


                                                    <div className="col-lg-6">
                                                        <div className="form-group">
                                                            <label className="form-label mt-4" htmlFor="features">
                                                                Features
                                                            </label>
                                                            <div className="form-control-wrap">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    id="features"
                                                                    placeholder="Add a feature and press Enter"
                                                                    value={currentFeature}
                                                                    onChange={(e) => setCurrentFeature(e.target.value)}
                                                                    onKeyDown={handleFeatureKeyDown}
                                                                />
                                                                <div className="mt-2">
                                                                    {formData.features.map((feature, index) => (
                                                                        <span key={index} className="btn btn-sm bg-primary text-white me-2 btn-link" onClick={() => handleDeleteFeature(index)}>
                                                                            {feature} &times;
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="d-flex justify-content-end buttons mt-5">
                                                            {id && (
                                                                <ConfirmButton
                                                                    className="btn btn-danger me-2"
                                                                    label="Delete"
                                                                    danger={true}
                                                                    action={handleDelete}
                                                                />
                                                            )}
                                                            <button
                                                                className="btn btn-primary"
                                                                onClick={handleSave}
                                                                disabled={loading || !formData.name || !formData.type}
                                                            >
                                                                {id ? "Save Changes" : "Add Subscription"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditSubscription;
